'use client';

import { PaybackConnected } from '@packages/order/src/components/Payback/PaybackConnected';
import { PaybackDisconnected } from '@packages/order/src/components/Payback/PaybackDisconnected';
import { PaybackReauthenticate } from '@packages/order/src/components/Payback/PaybackReauthenticate';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import {
  useLoyaltyApi,
  LoyaltyStatusResponseStatusEnum,
} from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import { defineMessages, useIntl } from 'react-intl';
import useSWR from 'swr';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { PaybackAccountDataFetchProvider } from '@packages/order/src/components/Payback/PaybackAccountDataFetchProvider';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';

const messages = defineMessages({
  paybackHeadline: {
    defaultMessage: 'Mein PAYBACK',
    id: 'Payback.Headline',
  },
  ErrorMessage: {
    id: 'PaybackPage.ErrorMessage',
    defaultMessage:
      'Entschuldige, irgendetwas ist schief gelaufen. Probiere es später noch einmal.',
  },
});

const PaybackPage = () => {
  const { replace } = useRouter();
  const {
    order: {
      paths: { myAccountOverview },
    },
  } = useConfig();
  const { locale, formatMessage } = useIntl();
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const { loggedIn, softLoggedIn } = useLoginState();
  const { addNotification } = useNotification();
  const loyaltyApi = useLoyaltyApi();
  const { data } = useSWR(
    loyaltyApi && loggedIn ? 'getLoyaltyConnectionStatus' : null,
    () =>
      loyaltyApi?.getLoyaltyConnectionStatus({
        ecLocale: locale,
      }),
    {
      revalidateOnFocus: false,
      onSuccess: (callbackData) => {
        if (callbackData?.errors) {
          addNotification({
            severity: 'error',
            message: callbackData.errors.elements[0].description,
            duration: 5000,
            onClose: () => {},
          });
        }
        if (callbackData?.warnings) {
          addNotification({
            severity: 'warning',
            message: callbackData.warnings.elements[0].description,
            duration: 5000,
            onClose: () => {},
          });
        }
      },
      onError: () =>
        addNotification({
          message: formatMessage(messages.ErrorMessage),
          severity: 'error',
          duration: 5000,
        }),
    },
  );
  const isApp = appView === 'true';
  useEffect(() => {
    // isApp conditional is required until LoginComponent is build in NextJS modules
    // do not remove this unless you are sure that LoginComponent is build in NextJS modules, then just return <LoginComponent />
    if ((!loggedIn && !isApp) || (softLoggedIn && loggedIn)) {
      replace(getLocalizedPath(myAccountOverview, locale));
    }
  }, [loggedIn, replace, locale, myAccountOverview, softLoggedIn, isApp]);
  return (
    <>
      <h1 className="text-color-text-dark text-size-h4 md:text-size-h3 px-2 pt-1.5 pb-1.5 md:pt-0">
        {formatMessage(messages.paybackHeadline)}
      </h1>
      {data?.status === LoyaltyStatusResponseStatusEnum.NoConnection && (
        <PaybackDisconnected enableSignIn />
      )}
      {data?.status === LoyaltyStatusResponseStatusEnum.ConnectionInactive && (
        <PaybackDisconnected enableSignIn={false} />
      )}
      {data?.status === LoyaltyStatusResponseStatusEnum.ConnectionEstablished && (
        <PaybackAccountDataFetchProvider>
          <PaybackConnected />
        </PaybackAccountDataFetchProvider>
      )}
      {data?.status === LoyaltyStatusResponseStatusEnum.ReauthenticationNeeded && (
        <PaybackReauthenticate />
      )}
    </>
  );
};
// eslint-disable-next-line import-x/no-default-export
export default PaybackPage;
